import React, { useState, useEffect } from 'react';
import { useUser, SignIn, UserButton } from '@clerk/clerk-react';
import Calculator from './components/Calculator';
import CustomersCalculator from './components/CustomersCalculator';
import MoreTools from './components/MoreTools';
import './App.css';

import { usePostHog } from 'posthog-js/react'

const MAX_FREE_USES = 3;

interface RetentionRates {
  first: number;
  second: number;
  third: number;
  subsequent: number;
}

function App() {

  const posthog = usePostHog()

  const { isLoaded, isSignedIn, user } = useUser();
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');
  const [usageCount, setUsageCount] = useState(0);
  const [showAuthContainer, setShowAuthContainer] = useState(false);
  const [activeTab, setActiveTab] = useState<'ltv' | 'customers' | 'more'>(() => {
    const savedTab = localStorage.getItem('activeTab');
    return (savedTab as 'ltv' | 'customers' | 'more') || 'ltv';
  });
  const [retentionRates, setRetentionRates] = useState<RetentionRates>(() => {
    const savedRates = localStorage.getItem('shared_retention_rates');
    return savedRates ? JSON.parse(savedRates) : {
      first: 95,
      second: 90,
      third: 85,
      subsequent: 10
    };
  });

  const [showInfoText, setShowInfoText] = useState(false);


  const [price, setPrice] = useState<number>(() => {
    const savedPrice = localStorage.getItem('shared_price');
    return savedPrice ? Number(savedPrice) : 100;
  });
  const [currentCustomerArpu, setCurrentCustomerArpu] = useState<number>(() => {
    const savedPrice = localStorage.getItem('shared_current_customer_arpu');
    return savedPrice ? Number(savedPrice) : 100;
  });

  useEffect(() => {
    // Load usage count from localStorage
    const savedCount = localStorage.getItem('usageCount');
    if (savedCount) {
      setUsageCount(parseInt(savedCount));
    }

    // Load theme preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setTheme(savedTheme as 'light' | 'dark');
    }
  }, []);

  useEffect(() => {
    // Update auth container visibility
    setShowAuthContainer(!isSignedIn && usageCount >= MAX_FREE_USES);
  }, [isSignedIn, usageCount]);

  useEffect(() => {
    // Save active tab to localStorage
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  const handleActiveTab = (tab: 'ltv' | 'customers' | 'more') => {
    setActiveTab(tab);
    const eventName = `change_tab_to_${tab}`;
    posthog.capture(eventName)
  };

  useEffect(() => {
    localStorage.setItem('shared_retention_rates', JSON.stringify(retentionRates));
  }, [retentionRates]);

  useEffect(() => {
    localStorage.setItem('shared_price', price.toString());
  }, [price]);

  useEffect(() => {
    localStorage.setItem('shared_current_customer_arpu', currentCustomerArpu.toString());
  }, [currentCustomerArpu]);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  const handleCalculation = () => {
    if (!isSignedIn) {
      const newCount = usageCount + 1;
      setUsageCount(newCount);
      localStorage.setItem('usageCount', newCount.toString());
    }
  };

  const handleShowInfoText = () => {
    setShowInfoText(!showInfoText);
    posthog.capture('click_info_text');
  };

  if (!isLoaded) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
        <p>...saassing it up for you...</p>
      </div>
    );
  }

  return (
    <div className={`app ${theme}-theme`}>
      <div className="container">
        <div className="header">
          <h1>SaaSSy Tools</h1>
          <div className="header-controls">
            <div className="theme-switch-wrapper">
              <label className="theme-switch">
                <input
                  type="checkbox"
                  checked={theme === 'dark'}
                  onChange={toggleTheme}
                />
                <span className="theme-slider round"></span>
              </label>
              <span className="theme-label">{theme === 'dark' ? '🌙' : '☀️'}</span>
            </div>
            {isSignedIn ? (
              <UserButton />
            ) : (
              <div className="free-uses">
                {MAX_FREE_USES - usageCount} calculations remaining
              </div>
            )}
          </div>
        </div>

         <p className="subtitle" onClick={() => handleShowInfoText()}>
          Calculate your SaaS metrics with mathematical precision
          <button className="info-button" onClick={() => handleShowInfoText()}>ⓘ</button>
        </p>
        {showInfoText && (
          <div className="info-text-container">
          <p className="info-text">Most LTV calculators get it wrong as they don't take negative compounding into account and don't really tell you what the time span of the "lifetime" is. Therefore, you get much higher LTV values than what you actually have. As a result, you might end up making wrong decisions and lose money.</p>
          <p className="info-text">For instance, the standard LTV value calculated by Stripe is ARPS divided by churn, but <a href="https://support.stripe.com/questions/calculating-subscriber-churn-rate-in-billing" target="_blank" rel="nofollow">they identify churn</a> as the number of cancelled subscriptions per their total number. This is misleading, because those rates are not constant over time. Churn at the beginning of the subscription cycle is higher than towards the end. So in reality you are losing revenue much quicker earlier in the cycle. There is a compounding negative effect occurring and it's not taken into account by the standard formulas.</p>
          <p className="info-text">Example: You had 900 customers at the end of December 2024 and another 100 signed up in January 2025 (at €10 ARPS). Let's say 100% of them paid the first cycle, but you had 90 clients cancelling from the existing cohort (9% churn in Stripe's terms). Stripe is going to tell you that your LTV is €10 / 0.09 ~ €111.00. You invest in ads where your CAC (acquisition cost) is €100 and expect a 10% ROI. Not so fast. </p>
          <p className="info-text">In fact, you should look into retention month-by-month to know how much money you're going to get. It might turn out that your churn is 20% after the first payment cycle (so only 80% of new subscribers pay a second month), 72% after the third one, and then, say, it's 8% churn month-by-month after that (seems like on average it would be 9% in a year). In that case, your LTV in the first year is €69 (10 + 8 + 7.2 + 6.6 + 6.1 + 5.6 + 5.2 + 4.8 + 4.4 + 4 + 3.7 + 3.5), and in the first 2 years it's €96, which is 15% less than what you expected (that could be your margin if you hoped to get your investment back in 2 years).</p>
          <p className="info-text">You'd also have fewer customers than you expect by the end of the year with this churn rate. You'd think with a churn of 9% you'd lose only 90 a year and gain a 100, so intuitively you'd have (100 - 90) * 12 = 120 more (1020) by the end of the year. But in reality, you'd have 943 customers (a gain of almost 50% less than expected) and that is also because of the compounding negative effect of the subscription cycles.</p>
          <p className="info-text">Another problem with standard LTV values is that they don't take into account a free trial model where only 50% of your signed up users might actually end up making the first payment. So your churn in this case is 50% in the first month, but they won't take that into account. So in our example that would lead to even lower results.</p>
          <p className="info-text">Our calculators are created to fix that and to let you plan exactly what you're going to get and when. This is useful for P&L assessments and for investment and acquisition planning. </p>
          </div>
        )}

        <div className="tabs">
          <button
            className={`tab ${activeTab === 'ltv' ? 'active' : ''}`}
            onClick={() => handleActiveTab('ltv')}
          >
            LTV Calculator
          </button>
          <button
            className={`tab ${activeTab === 'customers' ? 'active' : ''}`}
            onClick={() => handleActiveTab('customers')}
          >
            Revenue Calculator
          </button>
          <button
            className={`tab ${activeTab === 'more' ? 'active' : ''}`}
            onClick={() => handleActiveTab('more')}
          >
            More Tools
          </button>
        </div>

        {showAuthContainer ? (
          <div className="auth-container">
            <h2>Sign in to continue</h2>
            <p>You made {MAX_FREE_USES} calculations. Sign in to continue using the calculator (it's free).</p>
            <SignIn />
          </div>
        ) : (
          <>
            {activeTab === 'ltv' && (
              <Calculator
                onCalculate={handleCalculation}
                isAuthenticated={isSignedIn}
                retentionRates={retentionRates}
                setRetentionRates={setRetentionRates}
                price={price}
                setPrice={setPrice}
                currentCustomerArpu={currentCustomerArpu}
                setCurrentCustomerArpu={setCurrentCustomerArpu}
              />
            )}
            {activeTab === 'customers' && (
              <CustomersCalculator
                onCalculate={handleCalculation}
                isAuthenticated={isSignedIn}
                retentionRates={retentionRates}
                setRetentionRates={setRetentionRates}
                price={price}
                setPrice={setPrice}
                currentCustomerArpu={currentCustomerArpu}
                setCurrentCustomerArpu={setCurrentCustomerArpu}
              />
            )}
            {activeTab === 'more' && (
              <MoreTools isAuthenticated={isSignedIn} />
            )}
          </>
        )}
      </div>
      <footer className="footer">
        © made with sass in 2024 by <a href="https://noduslabs.com" target="_blank" rel="noopener noreferrer">Nodus Labs</a>  |  <a href="https://support.noduslabs.com" target="_blank" rel="noopener noreferrer">give us feedback!</a>
      </footer>
    </div>
  );
}

export default App;
